import { Button } from "@material-tailwind/react";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { useUserStore } from "../../Context/store";
import { useValidateCouponCode } from "../../Web-Hooks/Subscription/use-subscription";

const CouponCodePage = ({
  onCouponApplied,
  subscribedPlan,
  findPrice,
  stripeSubscription,
}) => {
  const [couponCode, setCouponCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [validationResult, setValidationResult] = useState({
    isValid: null,
    message: null,
    discountAmount: null,
    discountType: null,
    couponId: null,
  });

  const { user } = useUserStore();
  const { mutateAsync: validateCouponCode } = useValidateCouponCode();

  const handleValidationSuccess = (responseData) => {
    const newValidationResult = {
      isValid: true,
      message: "Coupon Code applied successfully!",
      discountAmount: responseData.discountAmount,
      discountType: responseData.discountType,
      couponId: responseData.couponId,
      invoiceJson: responseData?.invoiceJson,
    };
    setValidationResult(newValidationResult);
    onCouponApplied(couponCode, newValidationResult);
    toast.success("Coupon Code applied successfully!");
  };

  const handleValidationError = (message) => {
    setValidationResult({
      isValid: false,
      message,
      discountAmount: null,
      discountType: null,
      couponId: null,
    });
    onCouponApplied(null);
    toast.error(message);
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;

    setIsLoading(true);
    setValidationResult({ isValid: null, message: null });

    try {
      const response = await validateCouponCode({
        userId: user?.userId,
        couponCode,
        customerId: subscribedPlan?.customerId,
        newPriceId: findPrice?.priceId,
        subscriptionItemId: stripeSubscription?.items?.data[0]?.id,
        subscriptionId: stripeSubscription?.id,
      });

      if (response.data.data.isValid) {
        handleValidationSuccess(response.data.data);
      } else {
        handleValidationError(response.data.data.message);
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.data?.message || "Error validating coupon code";
      handleValidationError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      validateCoupon();
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setCouponCode(value);
    setValidationResult({ isValid: null, message: null });

    if (!value.trim()) {
      onCouponApplied(null);
    }
  };

  const getInputClasses = () => {
    const baseClasses =
      "flex-1 border p-2 w-full sm:w-auto rounded-lg focus:outline-none focus:ring-2 transition duration-200";

    if (validationResult.isValid === true) {
      return `${baseClasses} border-green-500 focus:ring-green-200`;
    }
    if (validationResult.isValid === false) {
      return `${baseClasses} border-red-500 focus:ring-red-200`;
    }
    return `${baseClasses} border-gray-300 focus:ring-primary focus:border-primary`;
  };

  const formatDiscountMessage = () => {
    if (!validationResult.discountAmount) return validationResult.message;
    return validationResult.message;
  };

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row items-center gap-2 w-full">
        <input
          type="text"
          placeholder="Enter coupon code"
          className={getInputClasses()}
          value={couponCode}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
        />

        <Button
          variant="gradient"
          disabled={!couponCode.trim() || isLoading}
          className={`whitespace-nowrap px-6 rounded-lg w-full sm:w-auto transition duration-200 ${
            couponCode.trim() && !isLoading
              ? "bg-primary text-white hover:bg-primary-dark"
              : "bg-gray-200 text-gray-500 cursor-not-allowed"
          }`}
          onClick={validateCoupon}
        >
          {isLoading ? "Validating..." : "Apply"}
        </Button>
      </div>

      {validationResult.message && (
        <div className="mt-2">
          <p
            className={`text-sm max-w-[360px] ${
              validationResult.isValid ? "text-green-600" : "text-red-600"
            }`}
          >
            {formatDiscountMessage()}
          </p>
        </div>
      )}
    </div>
  );
};

export default CouponCodePage;
